import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DollarSign, Plus, Pencil, Trash2, Loader2, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_TYPES = [
  { value: "defined_contribution", label: "Defined Contribution (401k-style)" },
  { value: "defined_benefit", label: "Defined Benefit (Traditional Pension)" },
  { value: "roth_ira", label: "Roth IRA" },
  { value: "sep_ira", label: "SEP IRA" },
  { value: "simple_ira", label: "SIMPLE IRA" },
  { value: "other", label: "Other" },
];

const blankForm = {
  name: "",
  provider: "",
  plan_type: "defined_contribution",
  employee_contribution_pct: 0,
  employer_match_pct: 0,
  employer_match_cap_pct: 0,
  vesting_years: 0,
  description: "",
  is_active: true,
};

export default function Pensions() {
  const [plans, setPlans] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [planRes, enrollRes] = await Promise.all([
        axios.get(`${API}/pensions/plans`, { headers }),
        axios.get(`${API}/pensions/enrollments`, { headers }),
      ]);
      setPlans(planRes.data || []);
      setEnrollments(enrollRes.data || []);
    } catch {
      toast.error("Failed to load pension plans");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openNew = () => {
    setEditingId(null);
    setForm(blankForm);
    setShowDialog(true);
  };

  const openEdit = (plan) => {
    setEditingId(plan.id);
    setForm({
      name: plan.name || "",
      provider: plan.provider || "",
      plan_type: plan.plan_type || "defined_contribution",
      employee_contribution_pct: plan.employee_contribution_pct || 0,
      employer_match_pct: plan.employer_match_pct || 0,
      employer_match_cap_pct: plan.employer_match_cap_pct || 0,
      vesting_years: plan.vesting_years || 0,
      description: plan.description || "",
      is_active: plan.is_active !== false,
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Plan name is required");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/pensions/plans/${editingId}`, form, { headers });
        toast.success("Pension plan updated");
      } else {
        await axios.post(`${API}/pensions/plans`, form, { headers });
        toast.success("Pension plan created");
      }
      setShowDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save plan");
    }
  };

  const removePlan = async (plan) => {
    if (!window.confirm(`Delete pension plan "${plan.name}"? This won't remove existing enrollments.`)) return;
    try {
      await axios.delete(`${API}/pensions/plans/${plan.id}`, { headers });
      toast.success("Plan deleted");
      fetchAll();
    } catch {
      toast.error("Failed to delete plan");
    }
  };

  const enrollmentCountByPlan = enrollments.reduce((acc, e) => {
    if (!e.plan_id) return acc;
    acc[e.plan_id] = (acc[e.plan_id] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-6 flex justify-center" data-testid="pensions-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="pensions-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <DollarSign className="h-8 w-8 text-primary" />
            Pensions &amp; Retirement
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure retirement plans and manage employee enrollments
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-pension-plan-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Plan
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card data-testid="kpi-plans">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase">Plans</CardDescription>
            <CardTitle className="text-3xl">{plans.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card data-testid="kpi-active-plans">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase">Active Plans</CardDescription>
            <CardTitle className="text-3xl">{plans.filter((p) => p.is_active).length}</CardTitle>
          </CardHeader>
        </Card>
        <Card data-testid="kpi-enrolled-employees">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase">Enrolled Employees</CardDescription>
            <CardTitle className="text-3xl">{enrollments.filter((e) => e.status === "active").length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Plans list */}
      <Card>
        <CardHeader>
          <CardTitle>Pension Plans</CardTitle>
          <CardDescription>Reusable plans that you can enroll employees in</CardDescription>
        </CardHeader>
        <CardContent>
          {plans.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-2 opacity-40" />
              <p>No pension plans yet.</p>
              <p className="text-sm">Click "New Plan" to create your first one.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {plans.map((p) => {
                const enrolled = enrollmentCountByPlan[p.id] || 0;
                return (
                  <div
                    key={p.id}
                    className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg border hover:bg-accent/30 transition-colors"
                    data-testid={`pension-plan-row-${p.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{p.name}</p>
                        <Badge variant="outline" className="text-xs capitalize">
                          {(p.plan_type || "").replace(/_/g, " ")}
                        </Badge>
                        {!p.is_active && (
                          <Badge variant="secondary" className="text-xs">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {p.provider ? `${p.provider} · ` : ""}
                        Employee {p.employee_contribution_pct || 0}% ·
                        Employer match {p.employer_match_pct || 0}%
                        {p.employer_match_cap_pct ? ` (cap ${p.employer_match_cap_pct}%)` : ""}
                        {p.vesting_years ? ` · ${p.vesting_years}y vesting` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {enrolled} enrolled
                      </Badge>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(p)}
                          data-testid={`edit-plan-${p.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removePlan(p)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-plan-${p.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="pension-plan-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Pension Plan" : "New Pension Plan"}</DialogTitle>
            <DialogDescription>
              Set up a plan that employees can enroll in. Per-employee contribution % can be
              overridden at enrollment time.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Plan Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="plan-name-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Provider</Label>
                <Input
                  value={form.provider}
                  onChange={(e) => setForm({ ...form, provider: e.target.value })}
                  placeholder="e.g. Fidelity"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Plan Type</Label>
              <Select value={form.plan_type} onValueChange={(v) => setForm({ ...form, plan_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLAN_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Employee %</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.employee_contribution_pct}
                  onChange={(e) => setForm({ ...form, employee_contribution_pct: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-1">
                <Label>Employer Match %</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.employer_match_pct}
                  onChange={(e) => setForm({ ...form, employer_match_pct: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-1">
                <Label>Match Cap %</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.employer_match_cap_pct}
                  onChange={(e) => setForm({ ...form, employer_match_cap_pct: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Vesting (years)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.vesting_years}
                  onChange={(e) => setForm({ ...form, vesting_years: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  id="is_active"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Plan details, vesting schedule notes, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-plan-btn">
              {editingId ? "Save Changes" : "Create Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
